/**
 * <engraving-field>
 *
 * Shows / hides the engraving inputs depending on whether the currently selected
 * variant's SKU matches the SKU configured in Theme settings → Products →
 * "Engraved variant SKU".
 *
 * The engraving service takes FOUR lines of FIFTEEN characters, so this renders
 * one visible input per line. Those inputs have no `name`: the only thing that
 * reaches the cart is a single hidden input holding the lines joined with " / ",
 * which keeps the order at one `Engraving` line item property exactly as before.
 *
 * Only line 1 is required — one-line engravings are normal. `required` lives on
 * a visible input rather than the hidden one because reportValidity() cannot
 * focus a hidden field.
 *
 * When the engraved variant is deselected every input is cleared, disabled and
 * hidden so nothing is submitted and add-to-cart validation is not blocked.
 *
 * Variant changes are detected in an architecture-agnostic way: it reacts to
 * the `variant:change` CustomEvent (preferred, carries the variant) and to the
 * bubbling `change` event of the form's `input[name="id"]` (used by other
 * product sections), resolving the SKU from an embedded variant id -> sku map.
 *
 * Public API (relied on by pawtag-product_template.liquid — keep these stable):
 *   applyVariantId(id) · lineItemProperty() -> {name, value}|null · enforceRequired() -> bool
 */
if (!customElements.get('engraving-field')) {
  customElements.define(
    'engraving-field',
    class EngravingField extends HTMLElement {
      constructor() {
        super();
        this.inputs = Array.prototype.slice.call(this.querySelectorAll('[data-engraving-input]'));
        this.valueInput = this.querySelector('[data-engraving-value]');
        this.targetSkus = (this.dataset.engravingSkus || '')
          .split(',')
          .map(function (s) { return s.trim(); })
          .filter(Boolean);
        this.separator = this.dataset.engravingSeparator || ' / ';
        this.maxLength = this.inputs.length
          ? parseInt(this.inputs[0].getAttribute('maxlength'), 10) || 15
          : 15;
        this.variantSkus = this.parseVariantSkus();

        this.onVariantChange = this.onVariantChange.bind(this);
        this.onFormChange = this.onFormChange.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.onInput = this.onInput.bind(this);
      }

      get form() {
        const id = this.dataset.formId;
        return (id && (document.forms[id] || document.getElementById(id))) || null;
      }

      /* The one input that carries `required`, and therefore the one that
         reportValidity() can focus. */
      get primaryInput() {
        return this.inputs[0] || null;
      }

      parseVariantSkus() {
        const node = this.querySelector('[data-engraving-variant-skus]');
        if (!node) return {};
        try {
          return JSON.parse(node.textContent) || {};
        } catch (e) {
          return {};
        }
      }

      connectedCallback() {
        this.inputs.forEach((input) => input.addEventListener('input', this.onInput));
        this.sync();

        const form = this.form;
        // Preferred signal: the variant:change CustomEvent (dispatched on the
        // product form, or the document when no form exists).
        if (form) {
          form.addEventListener('variant:change', this.onVariantChange);
        }
        document.addEventListener('variant:change', this.onVariantChange);
        // Fallback signal: the variant id input dispatches a bubbling `change`,
        // so listen on document to catch it regardless of where this field sits
        // relative to the form (and even if the form id can't be resolved).
        document.addEventListener('change', this.onFormChange);

        // Enforce the required field at submit time. Capturing on document means
        // this runs before the form's own submit handler, which matters for
        // forms rendered with `novalidate` (where native validation is off).
        document.addEventListener('submit', this.onSubmitCapture, true);

        // Sync with whatever variant is selected on load.
        this.evaluateFromSelectedId();
      }

      disconnectedCallback() {
        this.inputs.forEach((input) => input.removeEventListener('input', this.onInput));
        const form = this.form;
        if (form) {
          form.removeEventListener('variant:change', this.onVariantChange);
        }
        document.removeEventListener('variant:change', this.onVariantChange);
        document.removeEventListener('change', this.onFormChange);
        document.removeEventListener('submit', this.onSubmitCapture, true);
      }

      onInput() {
        this.sync();
        // Let a preview elsewhere on the page mirror what is being typed.
        this.dispatchEvent(new CustomEvent('engraving:change', {
          bubbles: true,
          detail: { lines: this.lines(), value: this.value() },
        }));
      }

      onSubmitCapture(event) {
        const primary = this.primaryInput;
        if (!primary || primary.disabled || !primary.required) return;
        if (event.target !== this.form) return;
        if (!primary.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          primary.reportValidity();
          primary.focus();
        }
      }

      onVariantChange(event) {
        const variant = event && event.detail && event.detail.variant;
        if (variant && typeof variant.sku !== 'undefined') {
          this.applySku(variant.sku);
          return;
        }
        this.evaluateFromSelectedId();
      }

      onFormChange(event) {
        const target = event && event.target;
        if (!target || target.name !== 'id' || !target.value) return;
        // Resolve the SKU straight from the changed id input + this field's own
        // variant map, so it works even when this.form can't be resolved.
        const key = String(target.value);
        if (Object.prototype.hasOwnProperty.call(this.variantSkus, key)) {
          this.applySku(this.variantSkus[key]);
        }
      }

      evaluateFromSelectedId() {
        const form = this.form;
        const idInput = form && form.querySelector('input[name="id"]');
        const id = idInput && idInput.value;
        if (id && Object.prototype.hasOwnProperty.call(this.variantSkus, id)) {
          this.applySku(this.variantSkus[id]);
        }
      }

      /* Public API for sections without a product form (e.g. the pawtag template),
         which manage variant selection and checkout with their own scripts. */
      applyVariantId(id) {
        const key = String(id);
        if (Object.prototype.hasOwnProperty.call(this.variantSkus, key)) {
          this.applySku(this.variantSkus[key]);
        }
      }

      get propertyName() {
        return this.dataset.engravingProperty || 'Engraving';
      }

      get isActive() {
        return !!this.primaryInput && !this.primaryInput.disabled;
      }

      /* Non-empty lines, trimmed, with blank lines in the middle dropped so a
         skipped line 2 doesn't engrave an empty gap. */
      lines() {
        return this.inputs
          .map(function (input) { return input.value.trim(); })
          .filter(Boolean);
      }

      value() {
        return this.lines().join(this.separator);
      }

      /* Returns true if it is OK to proceed, false (and shows a message) if the
         field is active, required and empty. */
      enforceRequired() {
        const primary = this.primaryInput;
        if (!primary || primary.disabled || !primary.required) return true;
        if (primary.checkValidity()) return true;
        primary.reportValidity();
        primary.focus();
        return false;
      }

      /* Returns { name, value } when active with a value, otherwise null. */
      lineItemProperty() {
        if (!this.isActive) return null;
        const value = this.value();
        if (!value) return null;
        return { name: this.propertyName, value: value };
      }

      applySku(sku) {
        const normalized = (sku == null ? '' : String(sku)).trim();
        this.setActive(normalized !== '' && this.targetSkus.indexOf(normalized) !== -1);
      }

      setActive(active) {
        this.classList.toggle('hidden', !active);
        if (!this.inputs.length) return;

        let cleared = false;
        this.inputs.forEach((input, index) => {
          if (active) {
            input.disabled = false;
            if (index === 0) input.required = true;
          } else {
            if (index === 0) input.required = false;
            input.disabled = true;
            if (input.value !== '') {
              input.value = '';
              cleared = true;
            }
          }
        });
        if (this.valueInput) this.valueInput.disabled = !active;
        if (cleared || active) this.sync();
      }

      /* Keeps the hidden submitted value and the per-line counters in step with
         the visible inputs. */
      sync() {
        if (this.valueInput) this.valueInput.value = this.value();
        this.inputs.forEach((input) => {
          const line = input.getAttribute('data-engraving-line');
          const counter = this.querySelector('[data-engraving-counter="' + line + '"]');
          if (counter) counter.textContent = input.value.length + '/' + this.maxLength;
        });
      }
    }
  );
}
